import apiClient from '../api/apiClient';

const BASE = '/api/v1/admin/purchase-deal-sheets';

export const TRANSACTION_TYPES = ['PURCHASE_OF_EQUITY_SHARES', 'SALE_OF_EQUITY_SHARES'];

export const TRANSACTION_TYPE_LABELS = {
  PURCHASE_OF_EQUITY_SHARES: 'Purchase of Equity Shares',
  SALE_OF_EQUITY_SHARES: 'Sale of Equity Shares',
};

export const MODE_OF_DELIVERY = ['DEMAT', 'PHYSICAL'];

export const MODE_OF_DELIVERY_LABELS = {
  DEMAT: 'Demat',
  PHYSICAL: 'Physical',
};

export const PAYMENT_STATUS = ['UNPAID', 'PARTIAL', 'PAID'];

export const PAYMENT_STATUS_LABELS = {
  UNPAID: 'Unpaid',
  PARTIAL: 'Partial',
  PAID: 'Paid',
};

const stripEmpty = (obj) =>
  Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== '' && v !== null && v !== undefined),
  );

const multipartConfig = {
  transformRequest: [
    (data, headers) => {
      if (typeof FormData !== 'undefined' && data instanceof FormData) {
        if (headers?.delete) headers.delete('Content-Type');
        else if (headers) {
          delete headers['Content-Type'];
          delete headers['content-type'];
        }
      }
      return data;
    },
  ],
};

const unwrap = (response) => response.data?.data ?? response.data;

const purchaseDealSheetService = {
  listSuppliers: async (params = {}) => {
    const response = await apiClient.get(`${BASE}/suppliers`, {
      params: stripEmpty({ page: 1, limit: 10, ...params }),
    });
    return unwrap(response);
  },

  getSupplierById: async (id) => {
    const response = await apiClient.get(`${BASE}/suppliers/${id}`);
    return unwrap(response);
  },

  listProducts: async (params = {}) => {
    const response = await apiClient.get(`${BASE}/products`, {
      params: stripEmpty({ page: 1, limit: 50, ...params }),
    });
    return unwrap(response);
  },

  getProductById: async (id) => {
    const response = await apiClient.get(`${BASE}/products/${id}`);
    return unwrap(response);
  },

  calculateAmounts: async (payload) => {
    const response = await apiClient.post(`${BASE}/calculate`, payload);
    return unwrap(response);
  },

  listDealSheets: async (params = {}) => {
    const defaults = {
      page: 1,
      limit: 10,
      sort_by: 'id',
      sort_order: 'desc',
    };
    const response = await apiClient.get(BASE, {
      params: stripEmpty({ ...defaults, ...params }),
    });
    return unwrap(response);
  },

  getDealSheetById: async (id) => {
    const response = await apiClient.get(`${BASE}/${id}`);
    return unwrap(response);
  },

  createDealSheet: async (payload) => {
    const response = await apiClient.post(BASE, payload);
    return unwrap(response);
  },

  updateDealSheet: async (id, payload) => {
    const response = await apiClient.patch(`${BASE}/${id}`, stripEmpty(payload));
    return unwrap(response);
  },

  deleteDealSheet: async (id) => {
    const response = await apiClient.delete(`${BASE}/${id}`);
    return response.data;
  },

  createPayment: async (dealSheetId, payload) => {
    const response = await apiClient.post(`${BASE}/${dealSheetId}/payments`, payload);
    return unwrap(response);
  },

  updatePayment: async (dealSheetId, paymentId, payload) => {
    const response = await apiClient.patch(
      `${BASE}/${dealSheetId}/payments/${paymentId}`,
      stripEmpty(payload),
    );
    return unwrap(response);
  },

  deletePayment: async (dealSheetId, paymentId) => {
    const response = await apiClient.delete(`${BASE}/${dealSheetId}/payments/${paymentId}`);
    return unwrap(response);
  },

  generateDealSheetPdf: async (id, { force_regenerate = false } = {}) => {
    const response = await apiClient.post(`${BASE}/${id}/generate-pdf`, { force_regenerate });
    return unwrap(response);
  },

  getDealSheetPdf: async (id) => {
    const response = await apiClient.get(`${BASE}/${id}/pdf`);
    return unwrap(response);
  },

  uploadSupplierDealSheet: async (dealSheetId, file) => {
    const formData = new FormData();
    formData.append('file', file, file.name || 'supplier-deal-sheet');
    const response = await apiClient.post(
      `${BASE}/${dealSheetId}/supplier-deal-sheet`,
      formData,
      multipartConfig,
    );
    return unwrap(response);
  },
};

export default purchaseDealSheetService;
